import { isEmpty } from 'lodash';
import { CALL_PRIVATE_API } from 'js/constants/api';
import * as actionTypes from '../../constants/actionTypes';
import { DELETE, GET, POST, PUT } from 'js/constants/requestMethods';
import { dataSetSchema, docSchema, domainSchema, marketplaceItemSchema } from 'js/schemas';
import Constants from 'js/Constants';
import { processSearchFilters } from 'js/lib/SearchFilterUtils';
import { SearchFilterViews } from 'js/constants/visibilityFilters';
import { SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT } from 'js/constants/schemaEditor';

export const loadAllDomains = () => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_ALL_DOMAINS_REQUEST,
        actionTypes.GET_ALL_DOMAINS_SUCCESS,
        actionTypes.GET_ALL_DOMAINS_FAILURE,
      ],
      endpoint: '/marketplace/domains/for_org',
      schema: [domainSchema],
      requestMethod: GET,
    },
  });
};

export const updateDomainInfo = (id, info) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATED_DOMAIN_INFO_REQUEST,
        actionTypes.UPDATED_DOMAIN_INFO_SUCCESS,
        actionTypes.UPDATED_DOMAIN_INFO_FAILURE,
      ],
      endpoint: `/marketplace/domains/${id}`,
      payload: info,
      schema: [domainSchema],
      requestMethod: PUT,
    },
  });
};

export const loadDomainItems =
  (id, page, sortCriteria, availableFilters) => (dispatch, getState) => {
    const {
      app: {
        visibilityFilters: { filters },
        accessControl: { mode: aclQueryMode },
      },
    } = getState();

    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];
    const { processedFilters } = processSearchFilters(
      filters[SearchFilterViews.AVAILABLE_DATA_SETS],
      availableFilters,
    );

    let requestMethod;
    let endpoint;
    let payload;

    if (isEmpty(processedFilters)) {
      requestMethod = GET;
      endpoint = `/marketplace/domains/${id}/items`;
      payload = {
        page,
        per_page: Constants.LISTING_ITEMS_PER_PAGE,
        sort_by: sortKey,
        sort_order: sortOrder,
      };
    } else {
      requestMethod = POST;
      endpoint = `/marketplace/domains/${id}/items/search?page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}&sort_by=${sortKey}&sort_order=${sortOrder}`;
      payload = { filters: processedFilters };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_DOMAIN_ITEMS_REQUEST,
          actionTypes.GET_DOMAIN_ITEMS_SUCCESS,
          actionTypes.GET_DOMAIN_ITEMS_FAILURE,
        ],
        endpoint,
        schema: [marketplaceItemSchema],
        requestMethod,
        payload,
        aclQueryMode,
        requestInfo: {
          id,
          view: SearchFilterViews.AVAILABLE_DATA_SETS,
        },
      },
    });
  };

export const loadDomainItem = (datasetId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_DOMAIN_ITEM_REQUEST,
        actionTypes.GET_DOMAIN_ITEM_SUCCESS,
        actionTypes.GET_DOMAIN_ITEM_FAILURE,
      ],
      endpoint: `/data_sets/${datasetId}`,
      requestMethod: GET,
      schema: marketplaceItemSchema,
      requestInfo: {
        datasetId,
      },
      payload: {
        expand: 1,
      },
    },
  });
};

export const requestDomainItemAccess = (domainId, itemId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_DOMAIN_ITEM_ACCESS_REQUEST,
        actionTypes.UPDATE_DOMAIN_ITEM_ACCESS_SUCCESS,
        actionTypes.UPDATE_DOMAIN_ITEM_ACCESS_FAILURE,
      ],
      endpoint: `/marketplace/domains/${domainId}/items/${itemId}/request_access`,
      requestMethod: POST,
      requestInfo: {
        domainId,
        itemId,
      },
    },
  });
};

export const loadOutgoingAccessRequests = () => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_OUTGOING_ACCESS_REQUESTS_REQUEST,
        actionTypes.GET_OUTGOING_ACCESS_REQUESTS_SUCCESS,
        actionTypes.GET_OUTGOING_ACCESS_REQUESTS_FAILURE,
      ],
      endpoint: '/approval_requests/access/requested',
      requestMethod: GET,
    },
  });
};

export const loadIncomingAccessRequests = () => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_INCOMING_ACCESS_REQUESTS_REQUEST,
        actionTypes.GET_INCOMING_ACCESS_REQUESTS_SUCCESS,
        actionTypes.GET_INCOMING_ACCESS_REQUESTS_FAILURE,
      ],
      endpoint: '/approval_requests/access/pending',
      requestMethod: GET,
    },
  });
};

export const loadOutgoingListingRequests = () => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_OUTGOING_LISTING_REQUESTS_REQUEST,
        actionTypes.GET_OUTGOING_LISTING_REQUESTS_SUCCESS,
        actionTypes.GET_OUTGOING_LISTING_REQUESTS_FAILURE,
      ],
      endpoint: '/approval_requests/listings/requested',
      requestMethod: GET,
    },
  });
};

export const loadIncomingListingRequests = () => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_INCOMING_LISTING_REQUESTS_REQUEST,
        actionTypes.GET_INCOMING_LISTING_REQUESTS_SUCCESS,
        actionTypes.GET_INCOMING_LISTING_REQUESTS_FAILURE,
      ],
      endpoint: '/approval_requests/listings/pending',
      requestMethod: GET,
    },
  });
};

export const loadListingRequestDataset = (dataSetId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_REQUEST_DATASET_REQUEST,
        actionTypes.GET_REQUEST_DATASET_SUCCESS,
        actionTypes.GET_REQUEST_DATASET_FAILURE,
      ],
      endpoint: `/approval_requests/data_sets/${dataSetId}`,
      requestMethod: GET,
      schema: marketplaceItemSchema,
      requestInfo: {
        dataSetId,
      },
      payload: {
        expand: 1,
      },
    },
  });
};

export const addDomainItem = (domainId, itemId) => (dispatch) => {
  const payload = { data_set_id: itemId };
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ADD_DOMAIN_ITEM_REQUEST,
        actionTypes.ADD_DOMAIN_ITEM_SUCCESS,
        actionTypes.ADD_DOMAIN_ITEM_FAILURE,
      ],
      endpoint: `/marketplace/domains/${domainId}/items`,
      requestMethod: POST,
      payload,
      requestInfo: {
        domainId,
        itemId,
      },
    },
  });
};

export const removeDomainItem = (domainId, itemId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_DOMAIN_ITEM_REQUEST,
        actionTypes.DELETE_DOMAIN_ITEM_SUCCESS,
        actionTypes.DELETE_DOMAIN_ITEM_FAILURE,
      ],
      endpoint: `/marketplace/domains/${domainId}/items/${itemId}`,
      requestMethod: DELETE,
      requestInfo: {
        domainId,
        itemId,
      },
    },
  });
};

export const addDomainItemRating = (id, domainId, rating) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ADD_DOMAIN_ITEM_RATING_REQUEST,
        actionTypes.ADD_DOMAIN_ITEM_RATING_SUCCESS,
        actionTypes.ADD_DOMAIN_ITEM_RATING_FAILURE,
      ],
      endpoint: `/data_sets/${id}/vote`,
      requestMethod: POST,
      payload: {
        vote: rating,
        rating_type: 'main',
      },
      requestInfo: {
        id,
        domainId,
        rating,
      },
    },
  })
    .then((response) => {
      dispatch({
        type: actionTypes.UPDATE_MARKETPLACE_ITEM,
        payload: {
          id,
          updates: {
            rating: response?.result.rating,
            rating_votes: response?.result.count,
          },
        },
      });
    })
    .catch((error) => {
      console.error('Rating API Error:', error);
    });
};

export const approveAccessRequest = (id, requestType) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.APPROVE_MARKETPLACE_REQUEST_REQUEST,
        actionTypes.APPROVE_MARKETPLACE_REQUEST_SUCCESS,
        actionTypes.APPROVE_MARKETPLACE_REQUEST_FAILURE,
      ],
      endpoint: `/approval_requests/${id}/approve`,
      requestMethod: PUT,
      requestInfo: {
        id,
        requestType,
      },
    },
  });
};

export const rejectAccessRequest = (id, requestType, reason) => (dispatch) => {
  const defaultReason = 'Access request rejected by Custodian';
  let payload = {};
  if (reason) {
    payload['reason'] = reason;
  } else {
    payload['reason'] = defaultReason;
  }

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.REJECT_MARKETPLACE_REQUEST_REQUEST,
        actionTypes.REJECT_MARKETPLACE_REQUEST_SUCCESS,
        actionTypes.REJECT_MARKETPLACE_REQUEST_FAILURE,
      ],
      endpoint: `/approval_requests/${id}/reject`,
      requestMethod: PUT,
      payload,
      requestInfo: {
        id,
        requestType,
      },
    },
  });
};

export const addDomain = (name) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ADD_DOMAIN_REQUEST,
        actionTypes.ADD_DOMAIN_SUCCESS,
        actionTypes.ADD_DOMAIN_FAILURE,
      ],
      endpoint: '/marketplace/domains',
      requestMethod: POST,
      schema: [domainSchema],
      payload: {
        name: name,
      },
    },
  });
};

export const deleteDomain = (domainId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_DOMAIN_REQUEST,
        actionTypes.DELETE_DOMAIN_SUCCESS,
        actionTypes.DELETE_DOMAIN_FAILURE,
      ],
      endpoint: `/marketplace/domains/${domainId}`,
      schema: [domainSchema],
      requestMethod: DELETE,
      requestInfo: {
        domainId,
      },
    },
  });
};

export const loadDomainCustodians = (domainId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_DOMAIN_CUSTODIANS_REQUEST,
        actionTypes.GET_DOMAIN_CUSTODIANS_SUCCESS,
        actionTypes.GET_DOMAIN_CUSTODIANS_FAILURE,
      ],
      endpoint: `/marketplace/domains/${domainId}/custodians`,
      requestMethod: GET,
      requestInfo: {
        domainId,
      },
    },
  });
};

export const addDomainCustodian = (domainId, userEmail) => (dispatch) => {
  const payload = {
    custodians: [{ email: userEmail }],
  };

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ADD_DOMAIN_CUSTODIAN_REQUEST,
        actionTypes.ADD_DOMAIN_CUSTODIAN_SUCCESS,
        actionTypes.ADD_DOMAIN_CUSTODIAN_FAILURE,
      ],
      endpoint: `/marketplace/domains/${domainId}/custodians`,
      requestMethod: PUT,
      payload,
      requestInfo: {
        domainId,
        userEmail,
      },
    },
  });
};

export const deleteDomainCustodian = (domainId, userId) => (dispatch) => {
  const payload = {
    custodians: [{ id: userId }],
  };

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_DOMAIN_CUSTODIAN_REQUEST,
        actionTypes.DELETE_DOMAIN_CUSTODIAN_SUCCESS,
        actionTypes.DELETE_DOMAIN_CUSTODIAN_FAILURE,
      ],
      endpoint: `/marketplace/domains/${domainId}/custodians`,
      requestMethod: DELETE,
      payload,
      requestInfo: {
        domainId,
        userId,
      },
    },
  });
};
